/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Base logic for model/bizos.
 */

import { oforEach } from '@zcode/nr-utils'
import invariant from 'tiny-invariant'
import type { O } from 'ts-toolbelt'
import { typeRegistry } from './reviveBizos'

type Defined<T> = Exclude<T, undefined>

// https://stackoverflow.com/questions/55541275/typescript-check-for-the-any-type
type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N
type IfReadonlyArrayOrArrayOfAny<T, Y, N> = T extends ReadonlyArray<infer Inner>
  ? 0 extends 1 & Inner
    ? Y
    : N
  : T extends Array<infer Inner>
    ? 0 extends 1 & Inner
      ? Y
      : N
    : N

export type BizoSpecificationObjectType<D extends object, S extends object> =
  | {
      new (d?: S): Bizo<D, S> & Readonly<D>
      fromJS?: (data?: S) => (Bizo<D, S> & Readonly<D>) | null | undefined
    }
  | HasFromJS<D, S>

type ArrayOrReadonlyArrayOfObjects<T extends object> = T[] | readonly T[]
type ObjectOrArrayOrReadonlyArrayOfObjects<T extends object> = T | T[] | readonly T[]

// TODO: name can also be a Path (see agent in the Listing bizo) -- that's likely
// difficult to type correctly, for the same reason that StringArray is, but I
// haven't actually attempted it here. TS 4.5 may help
export type BizoSpecificationObject<
  OuterS extends object,
  InnerD,
> = NonNullable<InnerD> extends ArrayOrReadonlyArrayOfObjects<infer InnerInnerD>
  ? {
      [OuterSKey in keyof OuterS]: NonNullable<
        OuterS[OuterSKey]
      > extends ArrayOrReadonlyArrayOfObjects<infer InnerS>
        ?
            | {
                name: OuterSKey
                type: BizoSpecificationObjectType<InnerInnerD, InnerS>
                isArray: true
                value?: InnerD
              }
            | {
                name: OuterSKey
                type: BizoSpecificationObjectType<
                  NonNullable<InnerD>,
                  NonNullable<OuterS[OuterSKey]>
                >
                isArray?: false
                value?: InnerD
              }
        : never
    }[keyof OuterS]
  : Defined<InnerD> extends ObjectOrArrayOrReadonlyArrayOfObjects<infer InnerInnerD>
    ? {
        [OuterSKey in keyof OuterS]: NonNullable<
          OuterS[OuterSKey]
        > extends ObjectOrArrayOrReadonlyArrayOfObjects<infer InnerS>
          ?
              | {
                  name: OuterSKey
                  type: BizoSpecificationObjectType<InnerInnerD, InnerS>
                  isArray: true
                  value?: InnerD
                }
              | {
                  name: OuterSKey
                  type: BizoSpecificationObjectType<Defined<InnerD>, NonNullable<OuterS[OuterSKey]>>
                  isArray?: false
                  value?: InnerD
                }
          : never
      }[keyof OuterS]
    : Defined<InnerD> extends object
      ? {
          [OuterSKey in keyof OuterS]: Defined<OuterS[OuterSKey]> extends object
            ? {
                name: OuterSKey
                type: BizoSpecificationObjectType<Defined<InnerD>, Defined<OuterS[OuterSKey]>> & {
                  fromJS: (data: null) => null
                }
                isArray?: false
                value?: InnerD
              }
            : never
        }[keyof OuterS]
      : Defined<InnerD> extends object | null
        ? {
            [OuterSKey in keyof OuterS]: Defined<OuterS[OuterSKey]> extends object | null
              ? {
                  name: OuterSKey
                  type: BizoSpecificationObjectType<
                    NonNullable<InnerD>,
                    NonNullable<OuterS[OuterSKey]>
                  >
                  isArray?: false
                  value?: InnerD
                }
              : never
          }[keyof OuterS]
        : IfAny<
            InnerD,
            {
              [OuterSKey in keyof OuterS]: IfAny<
                Defined<OuterS[OuterSKey]>,
                {
                  name: OuterSKey
                  type: BizoSpecificationObjectType<any, any>
                  isArray?: false
                  value?: InnerD
                },
                never
              >
            }[keyof OuterS],
            IfReadonlyArrayOrArrayOfAny<
              NonNullable<InnerD>,
              {
                [OuterSKey in keyof OuterS]: IfReadonlyArrayOrArrayOfAny<
                  NonNullable<OuterS[OuterSKey]>,
                  | {
                      name: OuterSKey
                      type: BizoSpecificationObjectType<any, any>
                      isArray: true
                      value?: InnerD
                    }
                  | {
                      name: OuterSKey
                      type: BizoSpecificationObjectType<
                        readonly any[] | any[],
                        readonly any[] | any[]
                      >
                      isArray?: false
                      value?: InnerD
                    },
                  never
                >
              }[keyof OuterS],
              never
            >
          >

interface HasFromJS<D extends object, S extends object> {
  // Not all fromJS methods return a Bizo
  fromJS: (data?: S) => Readonly<D> | null | undefined
}

export type StringKey<S extends object, ExpectedValue> = {
  [Key in keyof S]: S[Key] extends ExpectedValue ? Key : never
}[keyof S]
// TODO: Get StringArray to do something like that ^ to limit to paths that give the ExpectedValue
export type StringArray<S extends object, ExpectedValue> = Readonly<O.Paths<S>> | O.Paths<S>
/*
// attempt at that ^ TODO
// fails with ts2589 -- infinite depth -- might be fixable with TS 4.5 https://github.com/microsoft/TypeScript/pull/45711
import { A, L } from 'ts-toolbelt'
type PathsWithValueType<
  S extends object,
  Paths extends L.List<A.Key>,
  ExpectedValue,
  Depth extends number = 1 | 2 | 3 | 4
> = Paths extends unknown
  ? Depth extends unknown
    ? O.HasPath<S, Paths & { length: Depth }, ExpectedValue> extends 1
      ? Paths
      : never
    : never
  : never
export type StringArray<S extends object, ExpectedValue> = PathsWithValueType<
  S,
  O.Paths<S>,
  ExpectedValue
> */

export type Value<D extends object, Key extends keyof D, S extends object> =
  | StringKey<S, D[Key]>
  | StringArray<S, D[Key]>
  | BizoSpecificationObject<S, D[Key]>
  | ((this: Bizo<D, S> & Readonly<D>, data: Bizo<D, S> & Readonly<D>) => D[Key])
  | ((args: any) => D[Key])

// TODO: shouldn't this ^^^ be args: S instead of args: any

export type KeysWhoseValueIsNever<D extends object> = (string | number | symbol) &
  {
    [Key in keyof D]: D[Key] extends never | undefined
      ? 0 extends 1 & D[Key]
        ? never
        : Key
      : never
  }[keyof D]

export type Once<
  D extends object,
  S extends object,
  Superclass extends object,
> = Superclass extends D
  ? {
      [Key in Exclude<keyof D, KeysWhoseValueIsNever<D>>]: Value<D, Key, S>
    } & Partial<{
      [Key in KeysWhoseValueIsNever<D>]: Value<D, Key, S>
    }>
  : {
      [Key in Exclude<keyof D & keyof Superclass, KeysWhoseValueIsNever<D>>]?: Value<D, Key, S>
    } & {
      [Key in Exclude<keyof D, KeysWhoseValueIsNever<D> | keyof Superclass>]: Value<D, Key, S>
    } & Partial<{
        [Key in KeysWhoseValueIsNever<D>]: Value<D, Key, S>
      }>

export type PropertyDescriptors<D extends object, S extends object> = {
  [key in keyof D]: TypedPropertyDescriptor<D[key]> & ThisType<Bizo<D, S>>
}

// MT: D and B represent the bizo types that match the "Once" definition object
// if autoclass is extending an already-existing bizo, pass in the types for that bizo
// as D2 and B2

export function autoclass<
  D extends { _data: unknown },
  S extends object = Omit<D, '_data' | 'copy' | 'toJSON'>,
  B extends Bizo<D, S> = Bizo<D, S>,
  D2 extends object = D,
  S2 extends object = D2 extends D ? S : D2,
  B2 extends Bizo<D2, S2> = Bizo<D2, S2>,
>(
  name: string,
  klass:
    | {
        new (d?: S): B
        fromJS?: (data?: S) => Bizo<D, S> | null | undefined
      }
    | {
        new (d?: S2): B2
        fromJS?: (data?: S2) => Bizo<D2, S2> | null | undefined
      },
  once: Once<Omit<D, '_data' | 'copy' | 'toJSON'>, S, D2>,
  any?: Partial<D> | null
) {
  const props: PropertyDescriptors<D, S> = {} as PropertyDescriptors<D, S>
  oforEach(once, (getter, _target) => {
    const target: keyof D = _target as keyof D
    const functionGetter: (
      this: Bizo<D, S> & Readonly<D>,
      data: Bizo<D, S> & Readonly<D>
    ) => D[typeof target] = getter as () => D[typeof target]
    switch (Array.isArray(getter) ? 'array' : typeof getter) {
      case 'array':
        // getter is the name (or path to name) of an attribute on the inner data element
        props[target] = {
          get: function () {
            const val = (getter as StringArray<S, D[typeof target]>).reduce(
              // @ts-expect-error
              (s, p) => (s || {})[p],
              this._data
            ) as unknown as D[typeof target]
            Object.defineProperty(this, target, { value: val })
            return val
          },
        }
        break
      case 'string':
        // getter is the name (or path to name) of an attribute on the inner data element
        props[target] = {
          get: function () {
            const val = this._data[
              getter as StringKey<S, D[typeof target]>
            ] as unknown as D[typeof target]
            Object.defineProperty(this, target, { value: val })
            return val
          },
        }
        break
      case 'function':
        // getter() is an initialization function
        props[target] = {
          get: function () {
            const val = functionGetter.call(
              this as Bizo<D, S> & Readonly<D>,
              this as Bizo<D, S> & Readonly<D>
            )
            Object.defineProperty(this, target, { value: val })
            return val
          },
        }
        break
      case 'object':
        {
          // { name: string, type: class, isArray: boolean }
          const bizoSpecificationObjectGetter = getter as BizoSpecificationObject<
            S,
            D[typeof target] & object
          >
          props[target] = {
            get: function () {
              if (bizoSpecificationObjectGetter.value !== undefined) {
                return bizoSpecificationObjectGetter.value
              }
              const sVal =
                typeof bizoSpecificationObjectGetter.name === 'string'
                  ? this._data[bizoSpecificationObjectGetter.name]
                  : ((bizoSpecificationObjectGetter.name as unknown as StringArray<S, unknown>)
                      // @ts-expect-error
                      .reduce((s, p) => (s || {})[p], this._data) as unknown)
              let dVal: D[typeof target]
              if (sVal !== undefined && sVal !== null) {
                const Type = bizoSpecificationObjectGetter.type
                const factory: FromJSArrow<
                  D[typeof target] & object,
                  S[typeof bizoSpecificationObjectGetter.name] & object
                > = Type.fromJS
                  ? // @ts-expect-error each member of the union type has signatures, but none of those signatures are compatible with each other
                    (d: any) => Type.fromJS?.(d)
                  : // @ts-expect-error "Type might not be constuctable"
                    (d: any) => new Type(d)
                // @ts-expect-error
                dVal =
                  bizoSpecificationObjectGetter.isArray && Array.isArray(sVal)
                    ? // @ts-expect-error
                      sVal.map(factory)
                    : // @ts-expect-error we didn't confirm that sVal is an object
                      // base.spec.js has some weird tests where it uses the built-in
                      // String constructor as a "bizo" class. I don't think we do that
                      // sort of weird stuff in any actual bizos, but better safe than sorry,
                      // I guess
                      factory(sVal)
              } else {
                // @ts-expect-error
                dVal = sVal
              }
              Object.defineProperty(this, target, { value: dVal })
              return dVal
            },
          }
        }
        break
      default:
        return undefined
    }
  })
  if (any) {
    oforEach(any, (value, target) => {
      props[target] = { value }
    })
  }
  Object.defineProperties(klass.prototype, props)
  const betterKlass = klass as unknown as RealBizoType<
    IfAny<D2, D, keyof D2 extends keyof D ? D : D & D2>,
    IfAny<S2, S, keyof S2 extends keyof S ? S : S & S2>
  >
  typeRegistry.add(name, betterKlass)
  return betterKlass
}

export interface RealBizoType<D extends object, S extends object> {
  new (d?: S): Readonly<D> & Bizo<D, S>
  fromJS: ((data: S) => Readonly<D> & Bizo<D, S>) &
    ((data?: undefined) => undefined) &
    (<T extends 0 | 0n | false | '' | null>(data?: T) => T)
}

interface FromJSArrow<D extends object, S extends object> {
  (data: S): Bizo<D, S>
  (data: null): null
  (data?: undefined): undefined
  (data?: S | null | undefined): Bizo<D, S> | null | undefined
}

type WithTypeName<S extends object> = S & { __typename?: string }

/**
 * S is the snake-case object returned by the backend
 * D is the camel-case object used throughout FE code
 */
export class Bizo<D extends object, S extends object = D> {
  readonly _data: WithTypeName<S>

  /**
   * simplifies the ternary of creating a Bizo from js object or null/undefined
   *
   * @param data
   * @returns {*}
   */
  static fromJS<S extends object, B extends typeof Bizo<any, S>>(this: B, data: null): null
  static fromJS<S extends object, B extends typeof Bizo<any, S>>(
    this: B,
    data: undefined
  ): undefined
  static fromJS<S extends object, B extends typeof Bizo<any, S>>(this: B, data: S): InstanceType<B>
  static fromJS<S extends object, B extends typeof Bizo<any, S>>(
    this: B,
    data: S | null | undefined
  ): InstanceType<B> | null | undefined
  static fromJS<S extends object, B extends typeof Bizo<any, S>>(
    this: B,
    data: S | null | undefined
  ): InstanceType<B> | null | undefined {
    return data && (new this(data) as InstanceType<B>)
  }

  constructor(data = {} as WithTypeName<S>) {
    if (data && typeof data === 'object') {
      invariant(
        '__typename' in this && typeof this.__typename === 'string',
        `bizo ${this.constructor.name} is missing __typename`
      )
      invariant(
        !('__typename' in data) || data.__typename === this.__typename,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `_data has incorrect __typename ${data.__typename}, expected ${this.__typename}`
      )
      data.__typename = this.__typename
    }
    this._data = data
  }

  copy(): this {
    // @ts-expect-error
    return new this.constructor({ ...this._data })
  }

  toJSON() {
    return this._data
  }
}

/*
 let Foo = autoclass('Foo', class Foo extends Bizo {
   constructor(d) { super(d) }
   now() {
     return Date.now()
   }
   get age() {
     return Date.now() - 1484956063377
   }
 }, {
   firstName: 'first_name',
   lastName: 'last_name',
   age: function() {
     logger.debug('compute age:')
     return 7 * 7 * 7
   },
 }, {
   getAge: function() { return Date.now() - 86400 },
 })
 let f = new Foo({first_name: 'w', last_name: 'a'})
 logger.debug('1', f, '-------', JSON.stringify(f))
 logger.debug('FN', f.firstName, f.age, f.getAge())
 logger.debug('FN', f.firstName, f.age, f.getAge())
 logger.debug('2', f, '-------', JSON.stringify(f))
 class Bar extends Foo {
   get name() {
     return 'bar'
   }
 }
 */
